import React, { useState, useMemo } from 'react';
import { Trash2Icon } from 'lucide-react';

import { Button } from '@epoch-ui/components/Button/button';
import { SheetFooter } from '@epoch-ui/components/Sheet/sheet';
import { Separator } from '@epoch-ui/components/Separator';
import { GuestStatus } from 'types/guest.types';
import { EventType } from 'types/event.types';

import { useGuestSideSheetContext } from '../guestSideSheetContext';
import { RemoveGuestConfirmDialog, RsvpControlModal } from '../Modals';

import { RsvpControlDropdown, CHANGE_RSVP_OPTION } from './RsvpControlDropdown';

const registrationStatuses = [
  GuestStatus.CONFIRMED,
  GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY,
  GuestStatus.WAITLISTED
];

export const GuestSideSheetFooter = (): React.ReactNode => {
  const {
    eventOrSessionId,
    guestData: { guest, event, sessionStatuses },
    refetchGuestData,
    handleUpdateRefetch,
    closeSheet
  } = useGuestSideSheetContext();
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [isRsvpControlModalOpen, setIsRsvpControlModalOpen] = useState(false);

  const canModify: boolean = useMemo(() => {
    if (event.eventType === EventType.MULTI_SESSIONS) {
      const sessions = event.sessions || [];
      const pastSessionIds = sessions.filter(({ hasEnded }) => hasEnded).map(({ id }) => id);
      if (event.limitRegistration) {
        return !sessionStatuses.some(
          ({ id, status }) => pastSessionIds.includes(id) && registrationStatuses.includes(status)
        );
      } else {
        return !pastSessionIds.includes(eventOrSessionId);
      }
    } else {
      return !event.hasEnded;
    }
  }, [event, guest, sessionStatuses, eventOrSessionId]);

  if (!canModify) return null;

  const handleRsvpControlDropdownSelection = (selected: string): void => {
    if (selected === CHANGE_RSVP_OPTION.text) {
      setIsRsvpControlModalOpen(true);
    }
  };
  const handleGuestRemoval = (): void => {
    setOpenRemoveModal(false);
    closeSheet();
  };

  const handleRsvpUpdate = (): void => {
    refetchGuestData();
    handleUpdateRefetch();
  };

  return (
    <>
      <Separator className="-tw-mx-6 tw-w-[unset]" />
      <SheetFooter className="tw-sticky tw-bottom-0 tw-justify-between tw-bg-white">
        <RsvpControlDropdown status={guest.status} onSelect={handleRsvpControlDropdownSelection} />
        <Button
          size="xs"
          variant="ghost"
          className="tw-text-destructive-foreground hover:tw-text-destructive-foreground"
          onClick={() => setOpenRemoveModal(true)}
        >
          <Trash2Icon size={16} className="tw-mr-2" />
          Remove
        </Button>
      </SheetFooter>
      <RsvpControlModal
        open={isRsvpControlModalOpen}
        onOpenChange={() => setIsRsvpControlModalOpen(false)}
        eventOrSessionId={eventOrSessionId}
        guest={guest}
        event={event}
        sessionStatuses={sessionStatuses}
        onRsvpUpdate={handleRsvpUpdate}
      />
      <RemoveGuestConfirmDialog
        open={openRemoveModal}
        onOpenChange={setOpenRemoveModal}
        handleRemoveGuest={handleGuestRemoval}
      />
    </>
  );
};
